import React, { useMemo } from "react";
import { AccordionDetails, Box, TextField, Tooltip } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, gridClasses } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";

import { DateHelper } from "../../helpers";
import { IClearedInstruction, IClearedInstructionContainer } from "../../models";

export type ClearedInstructionDetailsProps = {
  clearedInstructionDetails?: IClearedInstructionContainer | null;
};

const StyledDataGrid = styled(DataGrid)(() => ({
  [`& .${gridClasses.row}`]: {
    "&:hover, &.Mui-hovered": {
      backgroundColor: "rgb(25 167 210 / 25%)",
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
}));

function getClearingCodeDetail(details?: IClearedInstruction | null) {
  const code = details?.clearingCode ?? "";
  const fullForm = details?.clearingCodeFullForm ?? "";

  if (!code && !fullForm) return "";

  return `${code} - ${fullForm}`;
}

function getClearingDateTime(details?: IClearedInstruction | null) {
  const date = details?.clearingDate;
  const time = details?.clearingTime;

  if (!date || !time) return "";

  return DateHelper.mergerAS400DateTime(date, time);
}

function getSortedDetails(
  clearedInstructionDetails?: IClearedInstructionContainer | null,
) {
  const sorted = [...(clearedInstructionDetails?.details ?? [])].sort(
    (a, b) =>
      (b.clearingDate ?? 0) - (a.clearingDate ?? 0) ||
      (b.clearingTime ?? 0) - (a.clearingTime ?? 0),
  );

  return sorted;
}

const defaultRenderCell = (params: GridRenderCellParams) => (
  <Tooltip title={params.value ?? ""}>
    <span>{params.value}</span>
  </Tooltip>
);

const columns: GridColDef[] = [
  {
    field: "clearingCode",
    headerName: "Clearing Code",
    flex: 1,
    minWidth: 220,
    renderCell: defaultRenderCell,
  },
  {
    field: "terminal",
    headerName: "Terminal",
    flex: 1,
    minWidth: 80,
    renderCell: defaultRenderCell,
  },
  {
    field: "dateTime",
    headerName: "Date/Time",
    flex: 1,
    minWidth: 190,
    renderCell: defaultRenderCell,
  },
  {
    field: "userName",
    headerName: "User Name",
    flex: 1,
    minWidth: 90,
    renderCell: defaultRenderCell,
  },
  {
    field: "piecesOpen",
    headerName: "Pieces Open",
    flex: 1,
    minWidth: 80,
    renderCell: defaultRenderCell,
  },
  {
    field: "piecesCleared",
    headerName: "Pieces Cleared",
    flex: 1,
    minWidth: 90,
    renderCell: defaultRenderCell,
  },
  {
    field: "freightBillPro",
    headerName: "Freight Bill/PRO #",
    flex: 1,
    minWidth: 130,
    renderCell: defaultRenderCell,
  },
  {
    field: "comments",
    headerName: "Comments",
    flex: 2,
    minWidth: 250,
    renderCell: defaultRenderCell,
  },
];

export default function ClearedInstructionDetails({
  clearedInstructionDetails,
}: Readonly<ClearedInstructionDetailsProps>) {
  const sortedDetails = useMemo(
    () => getSortedDetails(clearedInstructionDetails),
    [clearedInstructionDetails],
  );

  const rows = useMemo(() => {

    // Calculate cumulative pieces open
    const initialPiecesOpen = sortedDetails[0]?.openPieces ?? 0;
    let cumulativeClearedPieces = 0;

    return sortedDetails.map((detail, index) => {
      const calculatedPiecesOpen = index === 0 
        ? initialPiecesOpen 
        : initialPiecesOpen + cumulativeClearedPieces;
      
      const row = {
        id: index,
        clearingCode: getClearingCodeDetail(detail),
        terminal: detail?.terminal ?? "",
        dateTime: getClearingDateTime(detail),
        userName: detail?.clearingUser ?? "",
        piecesOpen: calculatedPiecesOpen,
        piecesCleared: detail?.clearedPieces ?? "",
        freightBillPro: detail?.clearingProNumber || "", // NOSONAR
        comments: detail?.clearingDescription ?? "",
      };

      // Add current row's cleared pieces to cumulative sum for next iteration
      cumulativeClearedPieces += Number(detail?.clearedPieces ?? 0);

      return row;
    });
  }, [sortedDetails]);

  const summaryValues = useMemo(() => {
    const openPieces = sortedDetails[0]?.openPieces ?? 0;
    const clearedPieces = sortedDetails.reduce(
      (sum, detail) => sum + (detail?.clearedPieces ?? 0),
      0
    );
    const totalPieces = openPieces + clearedPieces;

    return { totalPieces, openPieces, clearedPieces };
  }, [sortedDetails]);

  return (
    <AccordionDetails>
      <Box sx={{ width: "100%", minHeight: 200, padding: "10px 0" }}>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            label="Total Pieces"
            value={summaryValues.totalPieces}
            disabled
            size="small"
            sx={{ width: 150 }}
          />
          <TextField
            label="Open Pieces"
            value={summaryValues.openPieces}
            disabled
            size="small"
            sx={{ width: 150 }}
          />
          <TextField
            label="Cleared Pieces"
            value={summaryValues.clearedPieces}
            disabled
            size="small"
            sx={{ width: 150 }}
          />
        </Box>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooter={rows.length <= 10}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          rowHeight={35}
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5",
            },
          }}
        />
      </Box>
    </AccordionDetails>
  );
}
